import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { ICONS } from '../constants';
import { Shot } from '../types';

interface ShotNamePattern {
  prefix: string;
  startNumber: number;
  step: number;
  digits: number;
}

interface AutofillModalProps {
  shots: Shot[];
  startIndex: number;
  pattern: ShotNamePattern;
  onConfirm: (updates: Array<{ shotId: string; shotName: string }>) => Promise<void>;
  onCancel: () => void;
  isManual?: boolean; // If true, fill all shots by default
}

const AutofillModal: React.FC<AutofillModalProps> = ({
  shots,
  startIndex,
  pattern,
  onConfirm,
  onCancel,
  isManual = false
}) => {
  const { t } = useTranslation();
  const [isApplying, setIsApplying] = useState(false);
  
  // Draggable state
  const modalRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const dragStart = useRef({ x: 0, y: 0 });
  
  // Range selection
  const unfilledShots = shots.slice(startIndex).filter(shot => !shot.shotName);
  // If manual call, select all shots by default; otherwise default to first 10
  const defaultToIndex = isManual ? Math.max(0, unfilledShots.length - 1) : Math.min(unfilledShots.length - 1, 9);
  const [fromIndex, setFromIndex] = useState(0);
  const [toIndex, setToIndex] = useState(defaultToIndex);

  // Generate shot names based on pattern
  const generateShotName = (index: number): string => {
    const number = pattern.startNumber + (index * pattern.step);
    const paddedNumber = number.toString().padStart(pattern.digits, '0');
    return `${pattern.prefix}${paddedNumber}`;
  };

  // Get shots to fill in selected range
  const selectedShots = unfilledShots.slice(fromIndex, toIndex + 1);
  
  // Generate preview
  const preview = selectedShots.slice(0, 5).map((shot, idx) => ({
    shotId: shot.id,
    shotNumber: shot.shotNumber,
    newName: generateShotName(fromIndex + idx)
  }));

  // Generate all updates
  const generateAllUpdates = () => {
    return selectedShots.map((shot, idx) => ({
      shotId: shot.id,
      shotName: generateShotName(fromIndex + idx)
    }));
  };

  const handleConfirm = async () => {
    setIsApplying(true);
    try {
      const updates = generateAllUpdates();
      await onConfirm(updates);
    } catch (error) {
      console.error('Failed to apply autofill:', error);
    } finally {
      setIsApplying(false);
    }
  };

  // Draggable functionality
  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.drag-handle')) {
      setIsDragging(true);
      dragStart.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y
      };
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragStart.current.x,
          y: e.clientY - dragStart.current.y
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const patternDisplay = `${pattern.prefix}${'#'.repeat(pattern.digits)}`;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none flex items-start justify-start">
      <div
        ref={modalRef}
        className="pointer-events-auto bg-gray-800 rounded-lg shadow-2xl w-96 border border-gray-700"
        style={{
          position: 'absolute',
          left: `${position.x}px`,
          top: `${position.y}px`,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Header (Draggable) */}
        <div className="drag-handle flex items-center justify-between p-3 bg-gray-900 rounded-t-lg border-b border-gray-700 cursor-grab active:cursor-grabbing">
          <div className="flex items-center gap-2">
            <ICONS.RefreshCcw className="w-5 h-5 text-indigo-400" />
            <h3 className="text-sm font-bold text-white">
              {t('bidding.autofill')}
            </h3>
          </div>
          <button
            onClick={onCancel}
            disabled={isApplying}
            className="p-1 hover:bg-gray-700 rounded transition disabled:opacity-50"
            title="Close"
          >
            <ICONS.X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Pattern Info */}
        <div className="p-3 bg-gray-900/50 border-b border-gray-700">
          <div className="text-xs space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Pattern:</span>
              <span className="text-indigo-400 font-mono font-bold">{patternDisplay}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Step:</span>
              <span className="text-white font-bold">+{pattern.step}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Available shots:</span>
              <span className="text-white font-bold">{unfilledShots.length}</span>
            </div>
          </div>
        </div>

        {/* Range Selection */}
        <div className="p-3 border-b border-gray-700">
          <label className="text-xs font-semibold text-gray-400 uppercase mb-2 block">
            Select Range:
          </label>
          
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1">
              <label className="text-xs text-gray-500 block mb-1">From Shot:</label>
              <select
                value={fromIndex}
                onChange={(e) => {
                  const newFrom = parseInt(e.target.value);
                  setFromIndex(newFrom);
                  if (newFrom > toIndex) setToIndex(newFrom);
                }}
                className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs"
              >
                {unfilledShots.map((shot, idx) => (
                  <option key={shot.id} value={idx}>
                    Shot #{shot.shotNumber}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex-1">
              <label className="text-xs text-gray-500 block mb-1">To Shot:</label>
              <select
                value={toIndex}
                onChange={(e) => {
                  const newTo = parseInt(e.target.value);
                  setToIndex(newTo);
                  // If user selects a shot before fromIndex, adjust fromIndex
                  if (newTo < fromIndex) {
                    setFromIndex(newTo);
                  }
                }}
                className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs"
              >
                {unfilledShots.map((shot, idx) => (
                  <option key={shot.id} value={idx}>
                    Shot #{shot.shotNumber}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="text-center text-xs text-gray-400">
            Will fill <span className="text-white font-bold">{selectedShots.length}</span> shots
          </div>
        </div>

        {/* Preview */}
        <div className="p-3 max-h-48 overflow-y-auto">
          <div className="text-xs font-semibold text-gray-400 uppercase mb-2">
            Preview:
          </div>
          <div className="space-y-1">
            {preview.map((item) => (
              <div
                key={item.shotId}
                className="flex items-center justify-between p-2 bg-gray-900/50 rounded text-xs"
              >
                <span className="text-gray-500 font-mono">#{item.shotNumber}</span>
                <span className="text-gray-600 mx-2">→</span>
                <span className="text-indigo-400 font-mono font-medium">{item.newName}</span>
              </div>
            ))}
            {selectedShots.length > 5 && (
              <div className="text-center text-gray-500 text-xs py-1">
                ... and {selectedShots.length - 5} more
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-3 border-t border-gray-700 bg-gray-900/30">
          <button
            onClick={onCancel}
            disabled={isApplying}
            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded text-xs font-medium transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isApplying}
            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-xs font-medium transition disabled:opacity-50 flex items-center gap-1"
          >
            {isApplying ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                <span>Applying...</span>
              </>
            ) : (
              <>
                <ICONS.Check className="w-3 h-3" />
                <span>Apply ({selectedShots.length})</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutofillModal;

